/**
 * Chat configuration for easy adjustment of all protections and optimizations
 * Set any value to false/0 to disable that feature
 */

export const CHAT_CONFIG = {
  // === PROTECTION SETTINGS (can be completely disabled) ===
  
  // Abuse protection - set to false to disable entirely
  ENABLE_SPAM_PROTECTION: true,
  
  // Very high limits - only blocks obvious bots/spam
  SPAM_MESSAGE_THRESHOLD: 1000,     // 1000 messages/minute before blocking
  SPAM_SEARCH_THRESHOLD: 500,       // 500 searches/minute before blocking
  SPAM_BURST_ALLOWANCE: 500,        // Extra allowance for conversation spikes
  
  // === PERFORMANCE SETTINGS ===
  
  // Search debouncing - set to 0 to disable
  SEARCH_DEBOUNCE_MS: 150,          // Reduced to 150ms for faster response
  
  // Auto-refresh - set ENABLE_AUTO_REFRESH to false to disable
  ENABLE_AUTO_REFRESH: true,
  AUTO_REFRESH_INTERVAL_MS: 3 * 60 * 1000, // 3 minutes (reduced from 5)
  
  // SSE real-time updates - set to false to disable real-time features
  ENABLE_REAL_TIME_UPDATES: true,
  SSE_HEARTBEAT_INTERVAL_MS: 30000, // 30 seconds
  SSE_RECONNECT_MAX_ATTEMPTS: 10,   // Increased for reliability
  SSE_RECONNECT_BASE_DELAY_MS: 1000,
  
  // === DEVELOPMENT SETTINGS ===
  
  // Debug logging
  ENABLE_DEBUG_LOGGING: false,
  ENABLE_PERFORMANCE_LOGGING: false,
  
  // === EASY DISABLE FLAGS ===
  
  // Set these to true to disable ALL protections (for testing/debugging)
  DISABLE_ALL_RATE_LIMITING: false,
  DISABLE_ALL_DEBOUNCING: false,
  DISABLE_ALL_PROTECTIONS: false,

  // === FEATURE FLAGS ===
  ENABLE_UNIFIED_SEARCH: true,
};

/**
 * Helper function to check if a feature is enabled
 */
export function isFeatureEnabled(feature: keyof typeof CHAT_CONFIG): boolean {
  if (CHAT_CONFIG.DISABLE_ALL_PROTECTIONS) {
    return false;
  }
  
  return Boolean(CHAT_CONFIG[feature]);
}

/**
 * Helper function to get a numeric config value
 */
export function getConfigValue(key: keyof typeof CHAT_CONFIG): number {
  if (CHAT_CONFIG.DISABLE_ALL_PROTECTIONS) {
    return 0;
  }
  
  return Number(CHAT_CONFIG[key]) || 0;
}

/**
 * Easy way to disable all protections for high-traffic scenarios
 */
export function disableAllProtections(): void {
  Object.assign(CHAT_CONFIG, {
    ENABLE_SPAM_PROTECTION: false,
    SEARCH_DEBOUNCE_MS: 0,
    ENABLE_AUTO_REFRESH: false,
    DISABLE_ALL_PROTECTIONS: true,
  });
  
  console.warn('🚨 All chat protections disabled - ensure this is intentional!');
}

/**
 * Restore default protection settings
 */
export function enableDefaultProtections(): void {
  Object.assign(CHAT_CONFIG, {
    ENABLE_SPAM_PROTECTION: true,
    SEARCH_DEBOUNCE_MS: 150,
    ENABLE_AUTO_REFRESH: true,
    DISABLE_ALL_PROTECTIONS: false,
  });
  
  console.log('✅ Default chat protections restored');
}

// Export for easy access in other modules
export default CHAT_CONFIG;
